<template>
  <div id="insider-backtester-results">
    <v-card v-if="!results">
      <v-card-text class="text-body-2"> Backtest not run yet. </v-card-text>
    </v-card>

    <v-container v-else fluid pa-0>
      <!-- Buttons: Excel, Start/Stop tracking -->
      <v-row>
        <v-col cols="12">
          <v-btn
            :href="`/generated_backtesters/${results.excel_file}`"
            :small="$vuetify.display.xs"
            target="_blank"
            color="primary"
            class="ml-0"
          >
            Download Backtest Data to Excel
          </v-btn>
          <v-btn
            :href="`/generated_backtesters/${results.transaction_log}`"
            :small="$vuetify.display.xs"
            target="_blank"
            color="primary"
            class="ml-0"
          >
            Download Backtest Transaction Log
          </v-btn>
        </v-col>
      </v-row>

      <!-- Timeframe, Moving averages, Index selection, Backtest chart -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <!-- Timeframe, Index selection, Moving averages -->
            <v-card-text>
              <v-container fluid pa-0>
                <v-row align="center">
                  <!-- Indices & Moving averages -->
                  <v-col cols="12" xl="8" order-xl="2">
                    <v-row align="center" justify="end">
                      <!-- Index selection -->
                      <v-col order-xl="2">
                        <v-row align="center">
                          <v-col>
                            <v-select
                              v-model="chart.xAxis"
                              :items="chartIntervalItems"
                              label="Interval"
                              item-title="description"
                              return-object
                              hide-details
                              class="ma-0"
                              style="max-width: 150px"
                              @update:model-value="refresh()"
                            />
                          </v-col>
                          <v-col>
                            <v-select
                              v-model="chart.comparisonIndex"
                              :items="indices"
                              label="Comparison index"
                              item-title="description"
                              return-object
                              hide-details
                              class="ma-0"
                              style="max-width: 300px"
                              @update:model-value="refresh()"
                            />
                          </v-col>
                          <v-col class="text-no-wrap">
                            <v-checkbox
                              v-model="chart.showPercentLong"
                              label="Show % Long"
                              @update:model-value="refresh()"
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Moving averages -->
                      <v-col :mr-4="$vuetify.display.xl" class="shrink" order-xl="1">
                        <v-btn
                          variant="outlined"
                          small
                          class="ma-0"
                          @click="movingAveragesDialog = true"
                        >
                          Configure moving averages
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row>
                  <!-- Timeframe -->
                  <v-col cols="12" xl="4" order-xl="1">
                    <v-btn-toggle
                      v-model="timeframe"
                      :class="{ 'd-flex justify-space-between': $vuetify.display.xs }"
                      mandatory
                      class="elevation-0 transparent"
                      @update:model-value="refresh()"
                    >
                      <v-btn
                        v-for="item in timeframeItems"
                        :key="item.value"
                        :value="item"
                        :class="{ 'px-3': $vuetify.display.smAndUp }"
                        large
                        text
                      >
                        {{ item.description }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <!-- Backtest Chart -->
            <v-card-text>
              <div id="backtest_chart" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Backtest Table -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <v-data-table
                id="backtest-table"
                :headers="backtestTableHeaders"
                :items="backtestTableItems"
                hide-default-footer
              >
                <template #item="{ item }">
                  <tr>
                    <td>{{ item['name'] }}</td>
                    <td :class="[item['YTD'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['YTD']) }}
                    </td>
                    <td :class="[item['1Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['1Y']) }}
                    </td>
                    <td :class="[item['2Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['2Y']) }}
                    </td>
                    <td :class="[item['3Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['3Y']) }}
                    </td>
                    <td :class="[item['5Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['5Y']) }}
                    </td>
                    <td :class="[item['7Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['7Y']) }}
                    </td>
                    <td :class="[item['10Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['10Y']) }}
                    </td>
                    <td :class="[item['Total Return'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['Total Return']) }}
                    </td>
                    <td :class="[item['Annualized'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['Annualized']) }}
                    </td>
                    <td :class="[item['Std Dev'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['Std Dev']) }}
                    </td>
                    <td>{{ percent(item['Sortino']) }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Ending Portfolio -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text class="pa-6 bg-grey-darken-3 text-white">
              <h2 class="text-h5 mb-2 d-inline-flex align-center">
                <img
                  src="https://d27mjrcvcy56qq.cloudfront.net/images/ico_graph.png"
                  class="mr-2"
                />
                Ending Portfolio
              </h2>
              <div>
                Click the "Download Backtest Data to Excel" button above to see all stock activity
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Stocks Table & Ending Sector Allocation chart -->
      <v-row mb-2>
        <!-- Stocks Table -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <v-data-table
                id="backtest-stocks-table"
                :headers="stocksTableHeaders"
                :items="stocksTableItems"
                hide-default-footer
                items-per-page="-1"
              >
                <template #item="{ item }">
                  <!-- Default row -->
                  <tr v-if="!item.rowType">
                    <td>
                      <a
                        :href="`/stock/${item.stock_permalink}`"
                        :title="item.stock_name"
                        class="font-weight-bold"
                        target="_blank"
                        v-text="item.ticker"
                      />
                      {{ item.stock_name }}
                    </td>
                    <td>
                      <span v-if="item.percent_of_portfolio && item.percent_of_portfolio > 0">
                        {{ percent(GetCashPercentOfPortfolio(item.current_mv) * 100.0) }}
                      </span>
                    </td>
                    <td :class="[GetTransactionTotalReturn(item) > 0 ? 'text-green' : 'text-red']">
                      <span v-if="!item.is_hedge">
                        {{ percent(GetTransactionTotalReturn(item)) }}
                      </span>
                    </td>
                    <td>{{ item.date_purchased }}</td>
                  </tr>

                  <!-- Add the extra `Cash balance` row on the bottom -->
                  <tr v-if="item.rowType === 'cashBalance'">
                    <td>
                      Cash balance of
                      <span class="font-weight-medium">
                        {{ currency(Math.round(results.portfolio.cash_on_hand * 100) / 100) }}
                      </span>
                    </td>
                    <td>
                      {{
                        percent(GetCashPercentOfPortfolio(results.portfolio.cash_on_hand) * 100.0)
                      }}
                    </td>
                    <td />
                    <td />
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Ending Sector Allocation chart -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <div id="backtest_sector_chart" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Moving Averages dialog -->
    <v-dialog v-model="movingAveragesDialog" persistent width="600px">
      <v-card>
        <v-card-title class="text-h6 primary text-white"> Configure Moving Averages </v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="maToAdd"
            :rules="[
              value =>
                !moving_averages.some(ma => ma.months === value) ||
                `${value} month moving average already added`
            ]"
            type="number"
            placeholder="Add an X month moving average"
            solo
            class="mb-2"
            @keydown.enter="AddMovingAverage()"
          >
            <template #prepend-inner>
              <v-icon color="success" @click="AddMovingAverage()">
                {{ mdiPlus }}
              </v-icon>
            </template>
          </v-text-field>
          <v-card v-if="moving_averages.length > 0">
            <v-list class="py-0">
              <template v-for="(ma, i) in moving_averages" :key="`${i}-${ma.description}`">
                <v-divider v-if="i !== 0" />

                <v-list-item>
                  <v-list-item-title>
                    {{ ma.description }}
                  </v-list-item-title>

                  <template #append>
                    <v-btn icon @click="DeleteMovingAverage(ma)">
                      <v-icon color="error">
                        {{ mdiClose }}
                      </v-icon>
                    </v-btn>
                  </template>
                </v-list-item>
              </template>
            </v-list>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="CloseMovingAverageDialog()"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Modules
import Highcharts from 'highcharts'
import last from 'lodash/last' // returns the last element of a given array

// Mixins
import FormattersMixin from '@/mixins/formatters'
import { mdiClock, mdiClose, mdiPlus } from '@mdi/js'

export default {
  mixins: [FormattersMixin],

  data: () => ({
    mdiClock,
    mdiClose,
    mdiPlus,
    movingAveragesDialog: false,
    results: null,
    criteria: {},
    timeframe: null,
    timeframeItems: [
      {
        description: 'YTD',
        value: 1,
        requires_subscription: false,
        time: 1
      },
      {
        description: '1y',
        value: 2,
        requires_subscription: false,
        time: 12
      },
      {
        description: '2y',
        value: 8,
        requires_subscription: false,
        time: 24
      },
      {
        description: '3y',
        value: 3,
        requires_subscription: true,
        time: 36
      },
      {
        description: '5y',
        value: 4,
        requires_subscription: true,
        time: 60
      },
      {
        description: '7y',
        value: 5,
        requires_subscription: true,
        time: 84
      },
      {
        description: '10y',
        value: 6,
        requires_subscription: true,
        time: 120
      },
      {
        description: 'Max',
        value: 0,
        requires_subscription: true,
        time: 0
      }
    ],
    chart: {
      xAxis: null,
      comparisonIndex: null,
      showPercentLong: true
    },
    chartIntervalItems: [
      {
        value: 'yearly_entries',
        description: 'Yearly'
      },
      {
        value: 'quarterly_entries',
        description: 'Quarterly'
      },
      {
        value: 'monthly_entries',
        description: 'Monthly'
      }
    ],
    chartData: [],
    indices: [],
    maToAdd: null,
    moving_averages: [],
    starting_record: null,
    hedged_matrix: null,
    ending_hedged_transaction: null,
    is_compound_chart: true,
    backtestTableHeaders: [
      { title: null, key: 'name', sortable: false },
      { title: 'YTD', key: 'YTD', sortable: false },
      { title: '1Y', key: '1Y', sortable: false },
      { title: '2Y', key: '2Y', sortable: false },
      { title: '3Y', key: '3Y', sortable: false },
      { title: '5Y', key: '5Y', sortable: false },
      { title: '7Y', key: '7Y', sortable: false },
      { title: '10Y', key: '10Y', sortable: false },
      { title: 'Total Return', key: 'Total Return', sortable: false },
      { title: 'Annualized', key: 'Annualized', sortable: false },
      { title: 'Std Dev', key: 'Std Dev', sortable: false },
      { title: 'Sortino', key: 'Sortino', sortable: false }
    ],
    stocksTableHeaders: [
      { title: 'Stock Name', key: 'Stock Name', sortable: false },
      { title: '% of Portfolio', key: '% of Portfolio', sortable: false },
      { title: 'Total Return', key: 'Total Return', sortable: false },
      { title: 'Original Purchase Date', key: 'Original Purchase Date', sortable: false }
    ]
  }),

  computed: {
    backtestTableItems() {
      return [
        {
          name: this.hedged_matrix ? 'Unhedged Portfolio' : 'Whale(s)',
          YTD: last(this.starting_record.yearly_entries)[3] * 100,
          '1Y': this.GetYearlyPerformance(this.starting_record, 1),
          '2Y': this.GetYearlyPerformance(this.starting_record, 2),
          '3Y': this.GetYearlyPerformance(this.starting_record, 3),
          '5Y': this.GetYearlyPerformance(this.starting_record, 5),
          '7Y': this.GetYearlyPerformance(this.starting_record, 7),
          '10Y': this.GetYearlyPerformance(this.starting_record, 10),
          'Total Return': this.starting_record.performance * 100,
          Annualized: this.starting_record.annualized_performance * 100,
          'Std Dev': this.starting_record.standard_deviation * 100.0,
          Sortino: this.starting_record.sortino
        },
        // Conditionally insert the 'Hedged Portfolio' row
        ...(this.hedged_matrix
          ? [
              {
                name: 'Hedged Portfolio',
                YTD: last(this.hedged_matrix.yearly_entries)[3] * 100,
                '1Y': this.GetYearlyPerformance(this.hedged_matrix, 1),
                '2Y': this.GetYearlyPerformance(this.hedged_matrix, 2),
                '3Y': this.GetYearlyPerformance(this.hedged_matrix, 3),
                '5Y': this.GetYearlyPerformance(this.hedged_matrix, 5),
                '7Y': this.GetYearlyPerformance(this.hedged_matrix, 7),
                '10Y': this.GetYearlyPerformance(this.hedged_matrix, 10),
                'Total Return': this.hedged_matrix.performance * 100,
                Annualized: this.hedged_matrix.annualized_performance * 100,
                'Std Dev': this.hedged_matrix.standard_deviation * 100.0,
                Sortino: this.hedged_matrix.sortino
              }
            ]
          : []),
        {
          name: this.chart.comparisonIndex.description,
          YTD: last(this.chart.comparisonIndex.yearly_entries)[3] * 100,
          '1Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 1),
          '2Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 2),
          '3Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 3),
          '5Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 5),
          '7Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 7),
          '10Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 10),
          'Total Return': this.chart.comparisonIndex.performance * 100,
          Annualized: this.chart.comparisonIndex.annualized_performance * 100,
          'Std Dev': this.chart.comparisonIndex.standard_deviation * 100.0,
          Sortino: this.chart.comparisonIndex.sortino
        }
      ]
    },

    stocksTableItems() {
      return [...this.results.portfolio.holdings, { rowType: 'cashBalance' }]
    }
  },

  methods: {
    genResults(results, criteria) {
      this.results = results
      this.criteria = criteria
      this.indices = this.results.performance_matrices.slice(1) // slice removes 'Whales' entry

      if (this.results.performance_matrices.length > 0) {
        this.starting_record = this.results.performance_matrices[0]
      }
      if (this.results.performance_matrices.length > 1) {
        this.chart.comparisonIndex = this.results.performance_matrices[1]
      }

      this.timeframe = this.timeframeItems[7]
      this.chart.xAxis = this.chartIntervalItems[2]

      this.$nextTick(() => {
        this.refresh()
        this.loadSectorChart('backtest_sector_chart', this.results.portfolio.holdings)
      })
    },

    refresh() {
      this.chartData = []
      let max = 0
      const timeframe = this.timeframe.time
      const today = new Date()
      let earliestDate = new Date(today.getFullYear(), today.getMonth() - timeframe)

      if (this.timeframe.value === 1) {
        earliestDate = new Date(today.getFullYear(), 0, 1)
      }

      const hedgingHighlight = {
        name: 'Hedging is on',
        color: '#FFFF66',
        area: true,
        data: [],
        tooltip: false,
        interactive: false,
        useInteractiveGuideline: false,
        lineinteractive: false
      } // https://djaodjin.com/blog/two-background-colors-nvd3-line-chart.blog.html
      let maxY = 0
      // or bar chart - create new entry see http://nvd3.org/examples/linePlusBar.html
      const a = []

      const longPercentage = {
        name: '% Invested Long',
        data: [],
        area: false,
        color: '#ff3333',
        type: 'line',
        yAxis: 1,
        step: 'left',
        dashStyle: 'ShortDash',
        lineWidth: 1
      }

      if (this.results.performance_matrices.length > 1) {
        a.push(this.results.performance_matrices[0])
        if (this.hedged_matrix) {
          a.push(this.hedged_matrix)
          a[0].description = 'Unhedged Portfolio'
        }

        a.push(this.chart.comparisonIndex)
      }

      if (this.chart.xAxis.value === 'monthly_entries') {
        const colors = ['#FFCC0F', '#C79F25', '#705A11', '#8BA9D0', '#6A90C1', '#066CA9', '#004B8C']
        for (let movingindex = 0; movingindex < this.moving_averages.length; movingindex++) {
          const ma = this.moving_averages[movingindex]
          ma.data = []
          ma.name = ma.description
          if (movingindex < colors.length) ma.color = colors[movingindex]
        }
      }

      for (let i = 0; i < a.length; i++) {
        const p = a[i]
        if (p.months) {
          continue
        }
        let data = []
        let tempdata = []
        let last = null

        if (this.chart.xAxis.value === 'quarterly_entries') {
          tempdata = p['monthly_entries']
        } else tempdata = p[this.chart.xAxis.value] // yearly_entries, monthly_entries, etc

        if (timeframe !== 0) {
          // strip out data earlier than earliestDate
          for (let j = 0; j < tempdata.length; j++) {
            const entry = tempdata[j]
            let darray = null
            if (entry.ending_balance) {
              darray = entry.end_date.split('-')
            } else {
              darray = entry[1].split('-')
            }
            const s = new Date(darray[0], darray[1] - 1, darray[2])
            if (s.getTime() >= earliestDate.getTime()) {
              data.push(entry)
            } else last = entry
          }
        } else {
          data = tempdata
        }

        if (this.hedged_matrix && i === 1) {
          tempdata = []
          data.forEach(hentry => {
            tempdata.push([
              hentry.end_date,
              hentry.end_date,
              hentry.ending_balance,
              hentry.perf_month,
              hentry.hedge_transaction
            ])
          })
          data = tempdata
        }

        const obj = {
          name: p.description,
          data: [],
          area: true
        }

        if (i === 0) {
          obj.color = '#57aea5' // '#96d8cc'
        } else if (i === 1 && this.hedged_matrix) {
          obj.color = '#00cc00'
        } else {
          obj.color = '#111'
        }

        let compound = 0
        let start = p.starting_balance

        if (timeframe !== 0) {
          if (last) start = last[2]
          else start = data[0][2]
        }

        for (let j = 0; j < data.length; j++) {
          const entry = data[j]
          const d3data = {
            x: null,
            y: null,
            hedged: false
          }
          let s = entry[0]

          if (!isNaN(s) || s.length === 4) s = new Date(parseInt(s), 1, 1)
          else {
            const darray = s.split('-')
            s = new Date(darray[0], parseInt(darray[1]) - 1, darray[2])
          }

          d3data.x = s.getTime()

          if (entry.length === 5 && entry[4]) {
            d3data.hedged = true
          }

          if (this.is_compound_chart) {
            compound = ((entry[2] - start) / start) * 1.0
            d3data.y = compound
          } else d3data.y = entry[3]

          if (i === 0) {
            const percentry = { x: d3data.x, y: entry[4] }
            longPercentage.data.push(percentry)
          }

          obj.data.push(d3data)

          if (d3data.y > max) max = d3data.y
          if (d3data.y > maxY) maxY = d3data.y

          if (i === 0 && this.chart.xAxis.value === 'monthly_entries') {
            for (let movingindex = 0; movingindex < this.moving_averages.length; movingindex++) {
              const ma = this.moving_averages[movingindex]
              // ma will be obj with description and months, values
              // take average of current-ma.months and add to ma.values
              if (j >= ma.months) {
                // whale ma needs to be perf for past X months
                const base = data[j - ma.months][2]
                const maperf = (data[j][2] - base) / base
                // hedging comp ma needs to be avg of sums of perfs for past 12 monts
                // with base being mv from 12 months ago
                ma.data.push({
                  x: d3data.x,
                  y: maperf
                })
              } else {
                ma.data.push({
                  x: d3data.x,
                  y: null
                })
              }
            }
          }

          if (this.chart.xAxis.value === 'quarterly_entries') {
            j += 2
            if (j + 1 >= data.length) j = data.length - 1
          }
        }

        this.chartData.push(obj)
      }

      if (this.chart.showPercentLong && longPercentage && longPercentage.data.length > 0) {
        this.chartData.push(longPercentage)
      }

      if (this.chart.xAxis.value === 'monthly_entries') {
        for (let movingindex = 0; movingindex < this.moving_averages.length; movingindex++) {
          const ma = this.moving_averages[movingindex]
          this.chartData.push(ma)
        }

        if (this.hedged_matrix && this.results.performance_matrices[0].moving_averages.length > 0) {
          const ma = {
            data: [],
            name: 'Hedging Moving Avg'
          }
          const hedgingMa = this.results.performance_matrices[0].moving_averages
          const compData = this.results.performance_matrices[0].monthly_entries
          const diff = compData.length - hedgingMa.length
          hedgingHighlight.data = []
          for (let i = 0; i < compData.length; i++) {
            const entry = compData[i]
            let s = entry[0]
            if (!isNaN(s) || s.length === 4) s = new Date(parseInt(s), 1, 1)
            else {
              const darray = s.split('-')
              s = new Date(darray[0], parseInt(darray[1]) - 1, darray[2])
            }
            const x = s.getTime()
            if (i >= diff) {
              const maentry = hedgingMa[i - diff]
              ma.data.push({
                x,
                y: maentry.value
              })
              const base = compData[i - diff][2]
              const entryMaPerf = (entry[2] - base) / base
              if (maentry.value > entryMaPerf) {
                hedgingHighlight.data.push({
                  x,
                  y: max
                })
              } else {
                hedgingHighlight.data.push({
                  x,
                  y: null
                })
              }
            } else {
              ma.data.push({
                x,
                y: null
              })
              hedgingHighlight.data.push({
                x,
                y: null
              })
            }
          }
          this.chartData.push(ma)
          this.chartData.push(hedgingHighlight)
          // modify for highcharts here
        }
      }

      Highcharts.chart('backtest_chart', {
        chart: {
          type: 'area',
          backgroundColor: '#FFFFFF'
        },
        title: {
          text: ''
        },
        subtitle: {},
        xAxis: {
          allowDecimals: false,
          labels: {
            formatter() {
              const d = new Date(this.value)
              if (this.chart.xAxis.value === 'yearly_entries') return d.getFullYear()
              else return d.getFullYear() + '-' + (d.getMonth() + 1)
            }
          }
        },
        yAxis: [
          {
            title: {
              text: ''
            },
            labels: {
              formatter() {
                return this.value * 100.0 + '%'
              }
            }
          },
          {
            title: {
              text: '% Invested'
            },
            labels: {
              formatter() {
                return this.value * 100.0 + '%'
              }
            },
            opposite: true
          }
        ],
        tooltip: {
          shared: true,
          formatter() {
            const points = this.points
            const pointsLength = points.length
            const d = new Date(points[0].key)

            let tooltipMarkup = pointsLength
              ? '<span style="font-size: 10px">' +
                d.getFullYear() +
                '-' +
                (d.getMonth() + 1) +
                '</span><br/>'
              : ''
            let index
            let yValueKwh

            for (index = 0; index < pointsLength; index += 1) {
              yValueKwh = (points[index].y * 100.0).toFixed(2)

              tooltipMarkup +=
                '<span style="color:' +
                points[index].series.color +
                '">\u25CF</span> ' +
                points[index].series.name +
                ': <b>' +
                yValueKwh +
                '%</b><br/>'
            }

            return tooltipMarkup
          }
        },
        credits: {
          enabled: false
        },
        plotOptions: {
          area: {
            // pointStart: 1940,
            marker: {
              enabled: false,
              symbol: 'circle',
              radius: 2,
              states: {
                hover: {
                  enabled: true
                }
              }
            }
          }
        },
        series: this.chartData
      })
    },

    loadSectorChart(element, data) {
      const chartSeries = []
      const series = {
        name: 'Sectors',
        data: []
      }
      const h = {
        cashOnHand: {
          name: 'Cash on Hand',
          y: Math.round(this.results.portfolio.cash_on_hand * 100) / 100
        }
      }
      let totalMV = this.results.portfolio.cash_on_hand

      for (let i = 0; i < data.length; i++) {
        const stock = data[i]

        totalMV += stock.current_mv
      }

      for (let i = 0; i < data.length; i++) {
        const stock = data[i]

        if (stock.shares === 0) continue
        if (!h[stock.sector]) {
          h[stock.sector] = {
            name: stock.sector,
            y: 0
          }
        }
        h[stock.sector].y += stock.current_mv
      }

      for (const propertyName in h) {
        if (propertyName === undefined) continue
        const entry = h[propertyName]
        if (!entry.y) continue
        entry.y = (entry.y / totalMV) * 100.0
        series.data.push(entry)
      }

      chartSeries.push(series)

      Highcharts.chart(element, {
        chart: {
          type: 'pie',
          backgroundColor: '#FFFFFF'
        },
        title: {
          text: 'Sector Allocation'
        },
        xAxis: {
          type: 'category'
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Sector Percentage'
          }
        },
        tooltip: {
          pointFormat:
            '<span style="color:{series.color}">{series.name}</span>:  ({point.percentage:.0f}%)<br/>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: false
            },
            showInLegend: true
          }
        },
        credits: {
          enabled: false
        },
        series: chartSeries
      })
    },

    setTimeframe(timeframe) {
      this.timeframeProxy = timeframe.value
      this.refresh()
    },

    GetYearlyPerformance(record, numYears) {
      if (!record) return ''
      let perf = 0
      let start = 0
      let end = 0
      const numMonths = numYears * 12
      const months = record.monthly_entries
      if (months.length < numMonths) return 'N/A'
      if (months[months.length - numMonths].ending_balance) {
        start = months[months.length - numMonths].ending_balance
        end = months[months.length - 1].ending_balance
      } else {
        start = months[months.length - numMonths][2]
        end = months[months.length - 1][2]
      }
      perf = (end - start) / start
      return perf * 100.0
    },

    GetTransactionTotalReturn(transaction) {
      if (!transaction) return ''

      const basis = transaction.avg_pps * transaction.shares
      const gain = transaction.realized_gain + transaction.unrealized_gain

      return (gain / basis) * 100.0
    },

    GetCashPercentOfPortfolio(val) {
      let total = this.results.portfolio.cash_on_hand

      this.results.portfolio.holdings.forEach(holding => {
        total += holding.current_mv
      })

      return val / total
    },

    AddMovingAverage() {
      if (!this.moving_averages.some(ma => ma.months === this.maToAdd)) {
        const sortedArray = this.moving_averages
        sortedArray.push({
          description: `${this.maToAdd} month moving average`,
          months: this.maToAdd
        })
        this.moving_averages = sortedArray.sort(
          (ma1, ma2) => parseInt(ma1.months) > parseInt(ma2.months)
        )
        this.maToAdd = null
      }
    },

    DeleteMovingAverage(ma) {
      const index = this.moving_averages.indexOf(ma)
      if (index >= 0) {
        this.moving_averages.splice(index, 1)
      }
    },

    CloseMovingAverageDialog() {
      this.movingAveragesDialog = false
      this.refresh()
    }
  }
}
</script>

<style lang="scss">
#backtest-table,
#backtest-stocks-table {
  thead th {
    color: rgba(0, 0, 0, 0.87);
  }
}
</style>
